// ---------------------------------------------------------------------------------------------------------------------------------

import React, { useState, useEffect, useRef } from "react";
import DataTable from "react-data-table-component";
import toast from "react-hot-toast";
import HomeService from "../../../Services/HomeService";
import HttpClientXml from "../../../utils/HttpClientXml";
import moment from "moment";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { calculateDaysAgo, propertyUnits } from "../../../utils/Helper";
import Swal from "sweetalert2";

const headLineStyle = {
  textAlign: "center",
  fontSize: "30px",
  color: "#868e96",
  margin: "15px",
  fontWeight: "bold",
};

function ManageProperty() {
  const userRole = localStorage.getItem("role");
  const userCity = localStorage.getItem("city");
  const loggedUserID = localStorage.getItem("uid");

  const [properties, setProperties] = useState([]);
  const [allProperties, setAllProperties] = useState([]); // Store all loaded properties
  const [editProperty, setEditProperty] = useState(null);
  const [propertyType, setPropertyType] = useState([]);
  const [cities, setCities] = useState([]);
  const [localities, setLocalities] = useState([]);
  const [bhkOptions, setBhkOptions] = useState([]);
  const [facings, setFacings] = useState([]);

  const [floorOptions, setFloorOptions] = useState([]);
  const [images, setImages] = useState([]);

  const [features, setFeatures] = useState([]);
  const [featureInput, setFeatureInput] = useState("");

  const [salesStaff, setSalesStaff] = useState([]);
  const [surveyStaff, setSurveyStaff] = useState([]);
  const [telecallingStaff, setTelecallingStaff] = useState([]);
  const [dataentryStaff, setataentryStaff] = useState([]);
  const [searchText, setSearchText] = useState("");

  // Search states for dropdowns
  const [localitySearchTerm, setLocalitySearchTerm] = useState("");
  const [apartmentSearchTerm, setApartmentSearchTerm] = useState("");
  const [showLocalityDropdown, setShowLocalityDropdown] = useState(false);
  const [showApartmentDropdown, setShowApartmentDropdown] = useState(false);

  // Refs for dropdowns
  const localityRef = useRef(null);
  const apartmentRef = useRef(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProperties, setTotalProperties] = useState(0);
  const [limit, setLimit] = useState(1000);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // New state for contact type
  const [contactType, setContactType] = useState("");
  const [contactName, setContactName] = useState("");

  const [propertyTypes, setPropertyTypes] = useState([]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (localityRef.current && !localityRef.current.contains(event.target)) {
        setShowLocalityDropdown(false);
      }
      if (
        apartmentRef.current &&
        !apartmentRef.current.contains(event.target)
      ) {
        setShowApartmentDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  //fetch property Types
  const fetchAllPropertyTypes = async () => {
    try {
      const res = await HomeService.getPropertyTypes();
      console.log("PropertyType", res);
      setPropertyTypes(res?.data);
    } catch (error) {
      console.error("Error fetching property types:", error);
      toast.error("Failed to load property types!");
    }
  };

  useEffect(() => {
    fetchAllPropertyTypes();
  }, []);

  // Fetch Staffs
  const fetchAllStaff = async (cityId) => {
    try {
      const res = await HomeService.getStaffs();
      if (res?.data) {
        setSalesStaff(
          res?.data.filter(
            (item) => item.role === "sales" && item?.city?._id === cityId,
          ),
        );
        setTelecallingStaff(
          res?.data.filter(
            (item) => item.role === "telecalling" && item?.city?._id === cityId,
          ),
        );
        setSurveyStaff(
          res?.data.filter(
            (item) => item.role === "survey" && item?.city?._id === cityId,
          ),
        );
      }
    } catch (error) {
      console.error("Error fetching staffs:", error);
    }
  };

  // Fetch Properties with Pagination
  const fetchProperties = async (page = 1, loadMore = false) => {
    if (loadMore) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }

    try {
      // Pass page number to the API
      const response = await HomeService.getProperties(page, 1000);
      console.log("property list with pagination", response);

      // Update pagination info
      if (response?.pagination) {
        setCurrentPage(response.pagination.currentPage || page);
        setTotalPages(response.pagination.totalPages || 1);
        setTotalProperties(response.pagination.totalProperties || 0);
        setLimit(response.pagination.limit || 1000);
        setHasNextPage(response.pagination.hasNextPage || false);
        setHasPrevPage(response.pagination.hasPrevPage || false);
      }

      const formattedData = response?.data
        .sort((d1, d2) => new Date(d2.date) - new Date(d1.date))
        .map((property, index) => ({
          id: property._id,
          sln: (page - 1) * limit + index + 1,
          viewDate: moment(property?.date).format("DD/MM/YYYY"),
          propertyType: property?.propertytype?.propertyType,
          propertyFor: property?.propertyFor,
          city: property?.city?.name,
          cityId: property?.city?._id,
          locality: property?.locality?.name,
          address: property?.address,
          area: property?.area,
          unit: property?.unit,
          price: property?.price,
          priceUnit: property?.priceUnit,
          status: property?.status,
          facing: property?.facing,
          features: property?.features || [],
          floor: property?.floor,
          bhk: property?.bhk,
          remarks: property?.remarks,
          name: property?.name,
          contactType: property?.contactType,
          phoneNo: property?.phoneNo,
          telecaller: property?.telecaller?.name,
          surveyor: property?.surveyor?.name,
          dataEntryPerson: property?.dataEntryPerson?.name,
          aptName: property?.aptName,
          locationUrl: property?.locationUrl,
          chosen: property?.chosen,
          action: (
            <>
              <span title="Edit" onClick={() => handleEdit(property)}>
                <i
                  className="fa-solid fa-pen-to-square"
                  style={{
                    cursor: "pointer",
                    padding: "5px",
                    marginRight: "5px",
                    fontSize: "1.5em",
                    color: "rgb(133, 218, 79)",
                    backgroundColor: "#E9F3F7",
                  }}
                ></i>
              </span>
              <span title="Delete" onClick={() => handleDelete(property._id)}>
                <i
                  className="fa-solid fa-trash-can text-danger"
                  style={{
                    cursor: "pointer",
                    padding: "5px",
                    marginRight: "5px",
                    fontSize: "1.5em",
                    color: "#477DA5",
                    backgroundColor: "#E9F3F7",
                  }}
                ></i>
              </span>
            </>
          ),
        }));

      let filteredData = formattedData;

      if (userCity) {
        filteredData = formattedData.filter(
          (item) => item?.cityId === userCity,
        );
      }

      if (loadMore) {
        // Append new data when loading more
        setAllProperties((prev) => [...prev, ...filteredData]);
        setProperties((prev) => [...prev, ...filteredData]);
      } else {
        // Replace data for first load or page change
        setAllProperties(filteredData || []);
        setProperties(filteredData || []);
      }
    } catch (error) {
      console.error("Error fetching properties:", error);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  // Handle custom pagination page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    const isFilterActive =
      filterData?.startDate ||
      filterData?.endDate ||
      filterData?.city ||
      filterData?.locality ||
      filterData?.name ||
      filterData?.propertyType ||
      filterData?.propertyFor;

    if (isFilterActive) {
      filterPropertyList(page, false);
    } else {
      fetchProperties(page, false);
    }
  };

  // Load more data for the next 1000
  const loadMoreData = () => {
    const nextPage = currentPage + 1;
    if (nextPage <= totalPages) {
      handlePageChange(nextPage);
    }
  };

  // Load previous data
  const loadPrevData = () => {
    const prevPage = currentPage - 1;
    if (prevPage >= 1) {
      handlePageChange(prevPage);
    }
  };

  // Fetch Facings
  async function fetchFacings() {
    try {
      const res = await HomeService.getFacings();
      if (res?.data) {
        setFacings(res.data);
      }
    } catch (error) {
      console.error("Error fetching facings:", error);
    }
  }

  const fetchDropdownData = async () => {
    try {
      const [propertyTypes, citiesData, bhks, floors] = await Promise.all([
        HomeService.getPropertyTypes(),
        HomeService.getCities(),
      ]);
      setPropertyType(propertyTypes?.data || []);

      setCities(citiesData?.data || []);
      if (userCity) {
        setCities(citiesData.data.filter((item) => item._id === userCity));
      }
      setBhkOptions(bhks?.data || []);
      setFloorOptions(floors?.data || []);
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
    }
  };

  useEffect(() => {
    console.log("features array", features);
  }, [features]);

  const [imageLoading, setImageLoading] = useState(false);

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    const uploadedImages = [];

    for (let i = 0; i < files.length; i++) {
      const data = new FormData();
      data.append("image", files[i]);
      setImageLoading(true);
      try {
        const res = await HttpClientXml.fileUplode(
          "image-upload",
          "POST",
          data,
        );
        if (res?.image) {
          uploadedImages.push(res.image);
          setImageLoading(false);
        }
        setImageLoading(false);
      } catch (error) {
        toast.error("Failed to upload image: " + files[i].name);
        console.error("Error uploading image:", error);
        setImageLoading(false);
      }
    }

    setImages((prevImages) => [...prevImages, ...uploadedImages]);
    toast.success("Images uploaded successfully!");
  };

  const handleAddFeature = () => {
    if (featureInput.trim() !== "") {
      setFeatures((prevFeatures) => [...prevFeatures, featureInput.trim()]);
      setFeatureInput("");
    } else {
      toast.error("Feature cannot be empty");
    }
  };

  const handleRemoveFeature = (featureToRemove) => {
    setFeatures((prevFeatures) =>
      prevFeatures.filter((feature) => feature !== featureToRemove),
    );
  };

  const handleRemoveImage = (item) => {
    console.log("YYT248", editProperty.images, item);
    setImages((prevImages) => prevImages.filter((img) => img !== item));
  };

  // const handleDelete = async (id) => {
  //   try {
  //     await HomeService.deleteProperty(id);
  //     fetchProperties(currentPage, false);
  //     toast.success("Property deleted successfully!");
  //   } catch (error) {
  //     toast.error("Failed to delete property.");
  //     console.error("Error deleting property:", error);
  //   }
  // };

  const handleDelete = async (id) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#85da4f",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
        cancelButtonText: "Cancel",
        background: "#fff",
        backdrop: `
        rgba(0,0,0,0.4)
        left top
        no-repeat
      `,
      });

      // ইউজার যদি কনফার্ম বাটনে ক্লিক করে
      if (result.isConfirmed) {
        // লোডিং স্টেট দেখান
        Swal.fire({
          title: "Deleting...",
          text: "Please wait",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        // ডিলিট API কল

        await HomeService.deleteProperty(id);

        // সফল ডিলিটের পর ডেটা রিফ্রেশ করুন
        if (
          filterData?.city ||
          filterData?.locality ||
          filterData?.name ||
          filterData?.propertyType ||
          filterData?.propertyFor
        ) {
          await filterPropertyList();
        } else {
          await fetchProperties(currentPage, false);
        }

        // সাকসেস মেসেজ দেখান
        Swal.fire({
          title: "Deleted!",
          text: "Property has been deleted successfully.",
          icon: "success",
          confirmButtonColor: "#85da4f",
          timer: 2000,
          timerProgressBar: true,
        });
      }
    } catch (error) {
      console.error("Error deleting property:", error);

      // এরর মেসেজ দেখান
      Swal.fire({
        title: "Error!",
        text: "Failed to delete property. Please try again.",
        icon: "error",
        confirmButtonColor: "#85da4f",
      });
    }
  };

  const [editID, setEditID] = useState("");

  // Handle contact type change
  const handleContactTypeChange = (type) => {
    setContactType(type);
    // Clear the name field when switching contact types
    setContactName("");
  };

  // Get the dynamic label for the name field based on contact type
  const getNameFieldLabel = () => {
    switch (contactType) {
      case "Owner":
        return "Owner Name";
      case "Builder":
        return "Builder Name";
      case "Dealer":
        return "Dealer Name";
      default:
        return "Name";
    }
  };

  // Get the placeholder text for the name field
  const getNameFieldPlaceholder = () => {
    switch (contactType) {
      case "Owner":
        return "Enter Owner Name";
      case "Builder":
        return "Enter Builder Name";
      case "Dealer":
        return "Enter Dealer Name";
      default:
        return "Select contact type first";
    }
  };

  // const handleEdit = (property) => {
  //   console.log("HTR229", property);
  //   setEditID(property._id);
  //   setFeatures(property?.features);
  //   setImages(property?.images);
  //   setSelectedOption(property?.propertyFor);
  //   fetchLocalities(property?.city?._id);
  //   fetchAllStaff(property?.city?._id);

  //   // Set contact type and name based on existing data
  //   const contactType = property?.contactType || "";
  //   setContactType(contactType);

  //   // Set the name from the unified name field
  //   const contactName = property?.name || "";
  //   setContactName(contactName);

  //   setEditProperty({
  //     ...editProperty,
  //     date: property?.date,
  //     area: property?.area,
  //     unit: property?.unit,
  //     bhk: property?.bhk,
  //     floor: property?.floor,
  //     name: property?.name,
  //     contactType: property?.contactType,
  //     phoneNo: property?.phoneNo,
  //     price: property?.price,
  //     priceUnit: property?.priceUnit,
  //     remarks: property?.remarks,
  //     images: property?.images,
  //     features: property?.features,
  //     address: property?.address,
  //     facing: property?.facing?._id,
  //     city: property?.city?._id,
  //     locality: property?.locality?._id,
  //     propertyType: property?.propertytype?._id,
  //     salesPersonId: property?.salesPerson?._id,
  //     telecallerId: property?.telecaller?._id,
  //     surveyorId: property?.surveyor?._id,
  //     age: property?.age,
  //     aptName: property?.aptName,
  //     propertyFor: property?.propertyFor,
  //     locationUrl: property?.locationUrl,
  //   });

  //   const editSection = document.getElementById("edit-section");
  //   if (editSection) {
  //     editSection.scrollIntoView({
  //       behavior: "smooth",
  //       block: "start",
  //     });
  //   }
  // };

  const handleEdit = (property) => {
    const editUrl = `/property/edit/${property._id}`;
    window.open(editUrl, "_blank");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    //validate price and floor
    const floorPlusCount = editProperty?.floor?.split("+").length - 1;
    const pricePlusCount = editProperty?.price?.split("+").length - 1;
    if (floorPlusCount !== pricePlusCount) {
      toast.error("Floor count and price count is not matching");
      return;
    }

    // Validate contact type and name
    if (contactType && !contactName.trim()) {
      return toast.error(`Please enter ${getNameFieldLabel()}`);
    }

    if (!contactType && contactName.trim()) {
      return toast.error("Please select contact type");
    }

    // Prepare contact data based on selected contact type
    let contactData = {};
    if (contactType && contactName.trim()) {
      contactData = {
        contactType: contactType,
        name: contactName.trim(),
      };
    }

    const formattedProperty = {
      propertyType: editProperty?.propertyType,
      city: editProperty?.city,
      locality: editProperty?.locality,
      address: editProperty?.address,
      area: parseInt(editProperty?.area || 0),
      unit: editProperty?.unit,
      bhk: editProperty?.bhk,
      floor: editProperty?.floor,
      price: editProperty?.price,
      priceUnit: editProperty?.priceUnit,
      phoneNo: editProperty?.phoneNo,
      facing: editProperty?.facing,
      features: features,
      images: images,
      propertyFor: editProperty?.propertyFor,
      date: editProperty?.date,
      telecallerId: editProperty?.telecallerId,
      surveyorId: editProperty?.surveyorId,
      age: editProperty?.age,
      aptName: editProperty?.aptName,
      locationUrl: editProperty?.locationUrl,
      chosen: editProperty?.chosen || "",
      ...contactData,
    };

    console.log("SER367", formattedProperty);

    try {
      const res = await HomeService.updateProperty(editID, formattedProperty);
      if (res?.status) {
        // fetchProperties(currentPage, false);
        setEditProperty(null);
        setImages([]);
        setContactType("");
        setContactName("");
        toast.success("Property updated successfully!");
      }
    } catch (error) {
      toast.error("Failed to update property.");
      console.error("Error updating property:", error);
    }
  };

  // const toggleStatus = async (id, currentStatus) => {
  //   try {
  //     const newStatus = currentStatus === true ? false : true;
  //     const result = await HomeService.updatePropertyStatus(id, {
  //       status: newStatus,
  //     });
  //     console.log("result", result);
  //     // if (result?.status) {
  //     //   const updatedArray = properties?.map((item) =>
  //     //     item._id === id ? { ...item, status: !currentStatus } : item,
  //     //   );

  //     //   setProperties(updatedArray);
  //     // }
  //     // if (
  //     //   filterData?.city ||
  //     //   filterData?.locality ||
  //     //   filterData?.name ||
  //     //   filterData?.propertyType ||
  //     //   filterData?.propertyFor
  //     // ) {
  //     //   filterPropertyList();
  //     // } else {
  //     //   fetchProperties(currentPage, false);
  //     // }
  //     fetchProperties(currentPage, false);
  //     console.log("property after status update", properties);
  //     toast.success(`Property status updated to ${newStatus}!`);
  //   } catch (error) {
  //     toast.error("Failed to update property status.");
  //     console.error("Error updating status:", error);
  //   }
  // };

  // const toggleStatus = async (id, currentStatus) => {
  //   try {
  //     const newStatus = currentStatus === true ? false : true;
  //     const result = await HomeService.updatePropertyStatus(id, {
  //       status: newStatus,
  //     });

  //     console.log("result", result);

  //     // লোকাল স্টেট আপডেট করুন
  //     const updatedProperties = properties.map((property) =>
  //       property.id === id ? { ...property, status: newStatus } : property,
  //     );
  //     setProperties(updatedProperties);

  //     // allProperties স্টেটও আপডেট করুন
  //     const updatedAllProperties = allProperties.map((property) =>
  //       property.id === id ? { ...property, status: newStatus } : property,
  //     );
  //     setAllProperties(updatedAllProperties);

  //     toast.success(`Property status updated to ${newStatus}!`);
  //   } catch (error) {
  //     toast.error("Failed to update property status.");
  //     console.error("Error updating status:", error);
  //   }
  // };

  const toggleStatus = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === true ? false : true;
      const result = await HomeService.updatePropertyStatus(id, {
        status: newStatus,
      });

      console.log("result", result);

      // লোকাল স্টেট আপডেট করুন
      const updatedProperties = properties.map((property) =>
        property.id === id ? { ...property, status: newStatus } : property,
      );
      setProperties(updatedProperties);

      // allProperties স্টেটও আপডেট করুন
      const updatedAllProperties = allProperties.map((property) =>
        property.id === id ? { ...property, status: newStatus } : property,
      );
      setAllProperties(updatedAllProperties);

      // চেক করুন ফিল্টার অ্যাক্টিভ আছে কিনা
      const isFilterActive =
        filterData?.city ||
        filterData?.locality ||
        filterData?.name ||
        filterData?.propertyType ||
        filterData?.propertyFor;

      if (!isFilterActive) {
        // ফিল্টার অ্যাক্টিভ না থাকলে (getAll পেজ),
        // setTimeout ব্যবহার করে UI ব্লক না করে ব্যাকগ্রাউন্ডে রিফ্রেশ করুন
        setTimeout(() => {
          fetchProperties(currentPage, false);
        }, 500); // 500ms delay দিলাম, আপনি চাইলে কম/বেশি করতে পারেন
      }

      toast.success(`Property status updated to ${newStatus}!`);
    } catch (error) {
      toast.error("Failed to update property status.");
      console.error("Error updating status:", error);
    }
  };

  const role = localStorage.getItem("role");

  const columns = [
    { name: "Sl#", selector: (row) => row.sln, sortable: true },
    { name: "Date", selector: (row) => row.viewDate, sortable: true },
    { name: "Property", selector: (row) => row.propertyType, sortable: true },
    { name: "For", selector: (row) => row.propertyFor, sortable: true },
    { name: "City", selector: (row) => row.city, sortable: true },
    { name: "Locality", selector: (row) => row.locality },
    { name: "Apartment/Building", selector: (row) => row.aptName, wrap: true },
    { name: "Address", selector: (row) => row.address },
    {
      name: "Area",
      selector: (row) => `${row?.area} ${row?.unit || ""}`,
      sortable: true,
    },
    {
      name: "Price/Rent",
      cell: (row) => (
        <>
          <i
            className="fa fa-indian-rupee-sign"
            style={{ marginRight: "3px" }}
          ></i>
          {row.price} {row.priceUnit}
        </>
      ),
      sortable: true,
    },
    {
      name: "Basic",
      selector: (row) =>
        row.chosen
          ? row.chosen.charAt(0).toUpperCase() + row.chosen.slice(1)
          : "N/A",
      sortable: true,
    },
    ...(role !== "survey"
      ? [
          {
            name: "Status",
            cell: (row) => (
              <button
                className={`btn btn-${
                  row.status === true ? "success" : "danger"
                }`}
                onClick={() => toggleStatus(row.id, row.status)}
              >
                {row.status === true ? "Active" : "Inactive"}
              </button>
            ),
            width: "150px",
          },
          {
            name: "Action",
            selector: (row) => row.action,
            sortable: true,
            style: { textAlign: "center" },
          },
        ]
      : []),
  ];

  const [selectedOption, setSelectedOption] = useState("sales");
  const handleChange = (e) => {
    setSelectedOption(e.target.value);
  };

  // Fetch localities
  const fetchLocalities = async (cityId) => {
    try {
      const res = await HomeService.getLocalities();
      console.log("HYT71", res);

      if (res?.data) {
        setLocalities(res.data.filter((item) => item?.city?._id === cityId));
      }
    } catch (error) {
      console.error("Error fetching localities:", error);
    }
  };

  const [filterData, setFilterData] = useState({
    startDate: "",
    endDate: "",
    city: userCity || "",
    locality: "",
    name: "",
    propertyType: "",
    propertyFor: "",
  });

  const filterPropertyList = async (page = 1, loadMore = false) => {
    if (loadMore) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }

    try {
      const response = await HomeService.filterProperty(
        {
          city: filterData?.city,
          locality: filterData?.locality,
          apartment: filterData?.name,
          propertyType: filterData?.propertyType,
          propertyFor: filterData?.propertyFor,
        },
        page,
      );
      console.log("filterData", response);

      // Update pagination info
      if (response?.pagination) {
        setCurrentPage(response.pagination.currentPage || page);
        setTotalPages(response.pagination.totalPages || 1);
        setTotalProperties(response.pagination.totalProperties || 0);
        setLimit(response.pagination.limit || 1000);
        setHasNextPage(response.pagination.hasNextPage || false);
        setHasPrevPage(response.pagination.hasPrevPage || false);
      }

      if (response?.data && response.data.length > 0) {
        let pageData = response.data;

        // Apply filters
        if (filterData.startDate) {
          pageData = pageData.filter((item) => {
            const itemDate = new Date(item.date);
            return itemDate >= new Date(filterData.startDate);
          });
        }

        if (filterData.endDate) {
          pageData = pageData.filter((item) => {
            const itemDate = new Date(item.date);
            return itemDate <= new Date(filterData.endDate);
          });
        }

        if (filterData.city) {
          pageData = pageData.filter(
            (item) => item?.city?._id === filterData?.city,
          );
        }

        if (filterData.locality) {
          pageData = pageData.filter(
            (item) => item?.locality?._id === filterData?.locality,
          );
        }

        if (filterData.name) {
          pageData = pageData.filter((item) =>
            item?.aptName
              ?.toLowerCase()
              ?.includes(filterData?.name.toLowerCase()),
          );
        }

        const formattedData = pageData.map((property, index) => ({
          id: property._id,
          sln: (page - 1) * limit + index + 1,
          viewDate: moment(property?.date).format("DD/MM/YYYY"),
          propertyType: property?.propertytype?.propertyType,
          propertyFor: property?.propertyFor,
          city: property?.city?.name,
          cityId: property?.city?._id,
          locality: property?.locality?.name,
          address: property?.address,
          area: property?.area,
          unit: property?.unit,
          price: property?.price,
          priceUnit: property?.priceUnit,
          status: property?.status,
          facing: property?.facing,
          features: property?.features || [],
          floor: property?.floor,
          bhk: property?.bhk,
          remarks: property?.remarks,
          name: property?.name,
          contactType: property?.contactType,
          phoneNo: property?.phoneNo,
          telecaller: property?.telecaller?.name,
          surveyor: property?.surveyor?.name,
          dataEntryPerson: property?.dataEntryPerson?.name,
          aptName: property?.aptName,
          locationUrl: property?.locationUrl,
          chosen: property?.chosen,
          action: (
            <>
              <span title="Edit" onClick={() => handleEdit(property)}>
                <i
                  className="fa-solid fa-pen-to-square"
                  style={{
                    cursor: "pointer",
                    padding: "5px",
                    marginRight: "5px",
                    fontSize: "1.5em",
                    color: "rgb(133, 218, 79)",
                    backgroundColor: "#E9F3F7",
                  }}
                ></i>
              </span>
              <span title="Delete" onClick={() => handleDelete(property._id)}>
                <i
                  className="fa-solid fa-trash-can text-danger"
                  style={{
                    cursor: "pointer",
                    padding: "5px",
                    marginRight: "5px",
                    fontSize: "1.5em",
                    color: "#477DA5",
                    backgroundColor: "#E9F3F7",
                  }}
                ></i>
              </span>
            </>
          ),
        }));

        let filteredData = formattedData;
        if (userCity) {
          filteredData = formattedData.filter(
            (item) => item?.cityId === userCity,
          );
        }

        if (loadMore) {
          setAllProperties((prev) => [...prev, ...filteredData]);
          setProperties((prev) => [...prev, ...filteredData]);
        } else {
          setAllProperties(filteredData || []);
          setProperties(filteredData || []);
        }
      } else {
        if (!loadMore) {
          setAllProperties([]);
          setProperties([]);
        }
      }
    } catch (error) {
      console.error("Error fetching properties:", error);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  // Function to export data to Excel
  const exportToExcel = () => {
    console.log("HDF525", properties);

    const exportableData = properties?.map((item, index) => ({
      Sl: index + 1,
      Property_Type: item?.propertyType,
      Available_For: item?.propertyFor,
      City: item?.city,
      Locality: item?.locality,
      Apartment: item?.aptName,
      Address: item?.aptName + ", " + item?.address,
      Area: item?.area ? item?.area + " " + item?.unit : "",
      Floor: item?.floor,
      BHK: item?.bhk,
      Facing: item?.facing?.facing,
      Basic: item?.chosen
        ? item.chosen.charAt(0).toUpperCase() + item.chosen.slice(1)
        : "N/A",
      Price: item?.price ? item?.price + " " + item?.priceUnit : "",
      Name: item?.name,
      Contact_Type: item?.contactType,
      Phone: item?.phoneNo,
      Remarks: item?.remarks,
      Time: calculateDaysAgo(item?.viewDate) + " ago",
    }));

    console.log("EXP531", exportableData);

    const fileType =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    const fileExtension = ".xlsx";
    const fileName = `PROPERTIES${moment().format("DDMMYYYYhhmmss")}`;

    const ws = XLSX.utils.json_to_sheet(exportableData);
    const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: fileType });
    saveAs(data, fileName + fileExtension);
  };

  const [apartments, setApartments] = useState([]);
  // Fetch Apartments
  const fetchApartments = async () => {
    try {
      const res = await HomeService.getApartment();
      console.log("HYT86", res.data);

      if (res?.data) {
        setApartments(
          res.data.filter(
            (item) =>
              item?.city?._id === editProperty.city &&
              item?.locality?._id === editProperty.locality,
          ),
        );
      }
    } catch (error) {
      console.error("Error fetching localities:", error);
    }
  };

  // Fetch Apartments
  const fetchApartmentsForFilter = async () => {
    try {
      const res = await HomeService.getApartment();
      console.log("HYT86", res.data);

      if (res?.data) {
        setApartments(
          res.data.filter(
            (item) =>
              item?.city?._id === filterData.city &&
              item?.locality?._id === filterData.locality,
          ),
        );
      }
    } catch (error) {
      console.error("Error fetching localities:", error);
    }
  };

  useEffect(() => {
    fetchProperties(currentPage, false);
  }, [searchText]);

  const [localitiesForFilter, setLocalitiesForFilter] = useState([]);

  // Fetch localities
  const fetchLocalitiesForFilter = async () => {
    try {
      const res = await HomeService.getLocalities();
      console.log("HYT71", res);

      if (res?.data) {
        setLocalitiesForFilter(
          res.data.filter((item) => item?.city?._id === filterData.city),
        );
      }
    } catch (error) {
      console.error("Error fetching localities:", error);
    }
  };

  // Filtered lists based on search term
  const filteredLocalities = localitiesForFilter.filter((item) =>
    item.name?.toLowerCase().includes(localitySearchTerm.toLowerCase()),
  );

  const filteredApartments = apartments.filter((item) =>
    item.aptName?.toLowerCase().includes(apartmentSearchTerm.toLowerCase()),
  );

  useEffect(() => {
    fetchLocalitiesForFilter();
    fetchApartments();
  }, [editProperty?.city, editProperty?.locality]);

  useEffect(() => {
    fetchLocalitiesForFilter();
    fetchApartmentsForFilter();
  }, [filterData.city, filterData.locality]);

  useEffect(() => {
    fetchProperties(1, false);
    fetchDropdownData();
    fetchFacings();
  }, []);

  return (
    <div className="main_wrap" style={{ marginTop: "6rem" }}>
      <div className="container-fluid" id="edit-section">
        <div className="mt-5">
          <h2 className="text-center" style={headLineStyle}>
            Manage Property
          </h2>
          <hr />
          {editProperty ? (
            <section className="chartBox_area">
              <h3>View/Modify Property Details</h3>
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-lg-9">
                    <div className="row">
                      <div className="col-md-6 mt-3">
                        <label>
                          Property Type<span className="text-danger">*</span>
                        </label>
                        <select
                          className="form-control"
                          value={editProperty?.propertyType}
                          onChange={(e) =>
                            setEditProperty({
                              ...editProperty,
                              propertyType: e.target.value,
                            })
                          }
                          required
                        >
                          <option value="">Select Property Type</option>
                          {propertyType.map((type) => (
                            <option key={type._id} value={type._id}>
                              {type.propertyType}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-md-6 mt-3">
                        <label>
                          Available For<span className="text-danger">*</span>
                        </label>
                        <select
                          className="form-control"
                          value={editProperty?.propertyFor}
                          onChange={(e) =>
                            setEditProperty({
                              ...editProperty,
                              propertyFor: e.target.value,
                            })
                          }
                          required
                        >
                          <option value="">--Select--</option>
                          <option value="sale">Sale</option>
                          <option value="rent">Rent</option>
                        </select>
                      </div>

                      <div className="col-md-6 mt-3">
                        <label>
                          City<span className="text-danger">*</span>
                        </label>
                        <select
                          className="form-control"
                          value={editProperty?.city}
                          onChange={(e) => {
                            setEditProperty({
                              ...editProperty,
                              city: e.target.value,
                            });
                            fetchLocalities(e.target.value);
                            fetchAllStaff(e.target.value);
                          }}
                          required
                        >
                          <option value="">---Select---</option>
                          {cities.map((city, key) => (
                            <option key={key} value={city._id}>
                              {city.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-md-6 mt-3">
                        <label>
                          Locality<span className="text-danger">*</span>
                        </label>
                        <div className="custom-dropdown" ref={localityRef}>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search locality..."
                            value={
                              showLocalityDropdown
                                ? localitySearchTerm
                                : editProperty?.locality
                                  ? localities.find(
                                      (item) =>
                                        item._id === editProperty.locality,
                                    )?.name || ""
                                  : ""
                            }
                            onChange={(e) => {
                              setLocalitySearchTerm(e.target.value);
                              setShowLocalityDropdown(true);
                            }}
                            onClick={() => {
                              setShowLocalityDropdown(!showLocalityDropdown);
                              setLocalitySearchTerm("");
                            }}
                          />
                          {showLocalityDropdown && (
                            <div className="dropdown-list">
                              {filteredLocalities.length > 0 ? (
                                filteredLocalities.map((item) => (
                                  <div
                                    key={item._id}
                                    className={`dropdown-item ${
                                      editProperty?.locality === item._id
                                        ? "selected"
                                        : ""
                                    }`}
                                    onClick={() => {
                                      setEditProperty({
                                        ...editProperty,
                                        locality: item._id,
                                      });
                                      setShowLocalityDropdown(false);
                                      setLocalitySearchTerm("");
                                    }}
                                  >
                                    {item.name}
                                  </div>
                                ))
                              ) : (
                                <div className="dropdown-item no-results">
                                  No localities found
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-md-6 mt-3">
                        <label>Apartment/Building Name</label>
                        <div className="custom-dropdown" ref={apartmentRef}>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search apartment..."
                            value={
                              showApartmentDropdown
                                ? apartmentSearchTerm
                                : editProperty?.aptName || ""
                            }
                            onChange={(e) => {
                              setApartmentSearchTerm(e.target.value);
                              setShowApartmentDropdown(true);
                            }}
                            onClick={() => {
                              setShowApartmentDropdown(!showApartmentDropdown);
                              setApartmentSearchTerm("");
                            }}
                          />
                          {showApartmentDropdown && (
                            <div className="dropdown-list">
                              {filteredApartments.length > 0 ? (
                                filteredApartments.map((item, key) => (
                                  <div
                                    key={key}
                                    className={`dropdown-item ${
                                      editProperty?.aptName === item.aptName
                                        ? "selected"
                                        : ""
                                    }`}
                                    onClick={() => {
                                      setEditProperty({
                                        ...editProperty,
                                        aptName: item.aptName,
                                      });
                                      setShowApartmentDropdown(false);
                                      setApartmentSearchTerm("");
                                    }}
                                  >
                                    {item.aptName}
                                  </div>
                                ))
                              ) : (
                                <div className="dropdown-item no-results">
                                  No apartments found
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-md-6 mt-3">
                        <label>Age</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editProperty?.age}
                          onChange={(e) =>
                            setEditProperty({
                              ...editProperty,
                              age: e.target.value,
                            })
                          }
                          placeholder="Enter Age of the property"
                        />
                      </div>

                      {/* Contact Type Radio Buttons */}
                      <div className="col-md-12 mt-3">
                        <label>Contact Type</label>
                        <span className="text-danger">*</span>
                        <div
                          className="d-flex gap-4 mt-2"
                          style={{ gap: "15px" }}
                        >
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="contactType"
                              id="owner"
                              value="Owner"
                              checked={contactType === "Owner"}
                              onChange={() => handleContactTypeChange("Owner")}
                              required
                            />
                            <label className="form-check-label" htmlFor="owner">
                              Owner
                            </label>
                          </div>
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="contactType"
                              id="builder"
                              value="Builder"
                              checked={contactType === "Builder"}
                              onChange={() =>
                                handleContactTypeChange("Builder")
                              }
                            />
                            <label
                              className="form-check-label"
                              htmlFor="builder"
                            >
                              Builder
                            </label>
                          </div>
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="contactType"
                              id="dealer"
                              value="Dealer"
                              checked={contactType === "Dealer"}
                              onChange={() => handleContactTypeChange("Dealer")}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="dealer"
                            >
                              Dealer
                            </label>
                          </div>
                        </div>
                      </div>

                      {/* Dynamic Name Field based on Contact Type */}
                      <div className="col-md-6 mt-3">
                        <label>
                          {getNameFieldLabel()}
                          {contactType && (
                            <span className="text-danger">*</span>
                          )}
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={contactName}
                          onChange={(e) => setContactName(e.target.value)}
                          placeholder={getNameFieldPlaceholder()}
                          disabled={!contactType}
                          required={!!contactType}
                        />
                      </div>

                      <div className="col-md-6 mt-3">
                        <label>
                          Phone Number<span className="text-danger">*</span>
                        </label>
                        <input
                          type="tel"
                          className="form-control"
                          value={editProperty?.phoneNo}
                          onChange={(e) =>
                            setEditProperty({
                              ...editProperty,
                              phoneNo: e.target.value,
                            })
                          }
                          placeholder="Enter Phone Number"
                          required
                        />
                      </div>

                      {/* Features Input */}
                      <div className="col-md-6 mt-3">
                        <label>Features</label>
                        <div className="d-flex">
                          <input
                            type="text"
                            className="form-control"
                            value={featureInput}
                            onChange={(e) => setFeatureInput(e.target.value)}
                            placeholder="Enter a feature"
                          />
                          <button
                            type="button"
                            className="btn btn-secondary ml-2"
                            onClick={handleAddFeature}
                          >
                            Add
                          </button>
                        </div>
                        <div className="mt-2">
                          {features.map((feature, index) => (
                            <span
                              key={index}
                              className="badge badge-primary mr-2"
                              style={{
                                display: "inline-blda4ock",
                                border: "solid 1px #85da4f",
                                backgroundColor: "transparent",
                                color: "#000",
                                padding: "8px 12px",
                                borderRadius: "13px",
                                fontSize: "14px",
                                margin: "5px",
                                cursor: "pointer",
                                boxShadow: "0 2px 5px rgba(0, 0, 0, 0.2)",
                                transition: "all 0.3s ease",
                                fontWeight: "normal",
                              }}
                              onClick={() => handleRemoveFeature(feature)}
                            >
                              {feature}{" "}
                              <span
                                style={{
                                  marginLeft: "5px",
                                  fontWeight: "bold",
                                  color: "#ff0000",
                                }}
                              >
                                ×
                              </span>
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="col-md-6 mt-3">
                        <label>Facing</label>
                        <select
                          className="form-control"
                          value={editProperty?.facing}
                          onChange={(e) =>
                            setEditProperty({
                              ...editProperty,
                              facing: e.target.value,
                            })
                          }
                        >
                          <option value="">Select Facing</option>
                          {facings.map((facing) => (
                            <option key={facing._id} value={facing._id}>
                              {facing.facing}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-md-6 mt-3">
                        <label>Address</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editProperty?.address}
                          onChange={(e) =>
                            setEditProperty({
                              ...editProperty,
                              address: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="col-md-6 mt-3">
                        <label>Location URL</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editProperty?.locationUrl}
                          onChange={(e) =>
                            setEditProperty({
                              ...editProperty,
                              locationUrl: e.target.value,
                            })
                          }
                          placeholder="Enter Location URL of the property"
                        />
                      </div>
                      <div className="col-md-4 mt-3">
                        <label>
                          Area<span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={editProperty?.area}
                          onChange={(e) =>
                            setEditProperty({
                              ...editProperty,
                              area: e.target.value,
                            })
                          }
                          required
                        />
                      </div>
                      <div className="col-md-2 mt-3">
                        <label>
                          Unit<span className="text-danger">*</span>
                        </label>
                        <select
                          className="form-control"
                          value={editProperty?.unit}
                          onChange={(e) =>
                            setEditProperty({
                              ...editProperty,
                              unit: e.target.value,
                            })
                          }
                          required
                        >
                          <option value="">--Select--</option>
                          {propertyUnits()?.areaUnits.map((item, key) => {
                            return (
                              <option key={key} value={item?.value}>
                                {item?.name}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                      <div className="col-md-6 mt-3">
                        <label>BHK</label>
                        <select
                          className="form-control"
                          value={editProperty?.bhk}
                          onChange={(e) =>
                            setEditProperty({
                              ...editProperty,
                              bhk: e.target.value,
                            })
                          }
                        >
                          <option value="">--Select--</option>
                          {propertyUnits()?.bhkTypes.map((item, key) => {
                            return (
                              <option key={key} value={item}>
                                {item}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                      <div className="col-md-6 mt-3">
                        <label>Floor</label>
                        <select
                          className="form-control"
                          value={editProperty?.floor}
                          onChange={(e) =>
                            setEditProperty({
                              ...editProperty,
                              floor: e.target.value,
                            })
                          }
                        >
                          <option value="">--Select--</option>
                          {propertyUnits()?.floors.map((item, key) => {
                            return (
                              <option key={key} value={item}>
                                {item}
                              </option>
                            );
                          })}
                        </select>
                      </div>

                      <div className="col-md-4 mt-3">
                        <label>
                          Price<span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={editProperty?.price}
                          onChange={(e) =>
                            setEditProperty({
                              ...editProperty,
                              price: e.target.value,
                            })
                          }
                          required
                        />
                      </div>
                      <div className="col-md-2 mt-3">
                        <label>
                          Price<span className="text-danger">*</span>
                        </label>
                        <select
                          className="form-control"
                          value={editProperty?.priceUnit}
                          onChange={(e) =>
                            setEditProperty({
                              ...editProperty,
                              priceUnit: e.target.value,
                            })
                          }
                          required
                        >
                          <option value="">--Select--</option>
                          {propertyUnits()?.priceUnits.map((item, key) => {
                            return (
                              <option key={key} value={item?.value}>
                                {item?.name}
                              </option>
                            );
                          })}
                        </select>
                      </div>

                      <div className="col-md-6 mt-3">
                        <label htmlFor="chosen">Is the Property Basic</label>
                        <select
                          id="chosen"
                          className="form-control"
                          value={editProperty?.chosen || ""}
                          onChange={(e) =>
                            setEditProperty({
                              ...editProperty,
                              chosen: e.target.value,
                            })
                          }
                        >
                          <option value="">--Select--</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      </div>

                      <div className="col-md-12 mt-3">
                        <label>Upload Images + Videos</label>
                        {images?.length > 0 && (
                          <div className="d-flex flex-wrap">
                            {images?.map((img, index) => (
                              <>
                                <span
                                  key={index}
                                  className="badge badge-primary mr-2"
                                  style={{
                                    display: "inline-blda4ock",
                                    border: "solid 1px #85da4f",
                                    backgroundColor: "transparent",
                                    color: "#000",
                                    padding: "8px 12px",
                                    borderRadius: "13px",
                                    fontSize: "14px",
                                    margin: "5px",
                                    cursor: "pointer",
                                    boxShadow: "0 2px 5px rgba(0, 0, 0, 0.2)",
                                    transition: "all 0.3s ease",
                                    fontWeight: "normal",
                                  }}
                                  onClick={() => handleRemoveImage(img)}
                                >
                                  {img.match(/\.(jpeg|jpg|png|gif)$/i) ? (
                                    <img
                                      src={img}
                                      alt={`media-${index}`}
                                      width="320"
                                      height="240"
                                    />
                                  ) : (
                                    <video width="320" height="240" controls>
                                      <source src={img} type="video/mp4" />
                                    </video>
                                  )}
                                  <span
                                    style={{
                                      marginLeft: "5px",
                                      fontWeight: "bold",
                                      color: "#ff0000",
                                      fontSize: "2em",
                                    }}
                                  >
                                    ×
                                  </span>
                                </span>
                              </>
                            ))}
                          </div>
                        )}
                        <input
                          type="file"
                          className="form-control"
                          onChange={handleImageUpload}
                          multiple
                          accept="image/*,video/*,.mkv"
                        />
                      </div>

                      <div className="col-md-12 mt-3">
                        <label>Remarks</label>
                        <textarea
                          className="form-control"
                          value={editProperty?.remarks}
                          onChange={(e) =>
                            setEditProperty({
                              ...editProperty,
                              remarks: e.target.value,
                            })
                          }
                          placeholder="Enter remarks (if any)"
                        ></textarea>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-3">
                    <div className="row">
                      <div className="col-md-12 mt-3">
                        <label htmlFor="startDate">
                          Date<span className="text-danger">*</span>
                        </label>
                        <input
                          type="date"
                          id="date"
                          className="form-control"
                          value={editProperty?.date?.split("T")[0]}
                          onChange={(e) =>
                            setEditProperty({
                              ...editProperty,
                              date: e.target.value,
                            })
                          }
                          required
                        />
                      </div>

                      <div className="col-lg-12 mt-3">
                        <label>Telecaller Staff</label>
                        <select
                          className="form-control"
                          value={editProperty?.telecallerId}
                          onChange={(e) =>
                            setEditProperty({
                              ...editProperty,
                              telecallerId: e.target.value,
                            })
                          }
                        >
                          <option value="">---Select---</option>
                          {telecallingStaff.map((item, key) => (
                            <option key={key} value={item._id}>
                              {`${item?.name} (${item?.designation?.name})`}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-lg-12 mt-3">
                        <label>Survey Staff</label>
                        <select
                          className="form-control"
                          value={editProperty?.surveyorId}
                          onChange={(e) =>
                            setEditProperty({
                              ...editProperty,
                              surveyorId: e.target.value,
                            })
                          }
                        >
                          <option value="">---Select---</option>
                          {surveyStaff.map((item, key) => (
                            <option key={key} value={item._id}>
                              {`${item?.name} (${item?.designation?.name})`}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-lg-12 mt-3">
                        <button
                          type="submit"
                          className="btn btn-primary mt-3"
                          disabled={imageLoading || isLoading}
                        >
                          {imageLoading ? "Uploading Media..." : "Save Changes"}
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary mt-3"
                          style={{ marginLeft: "11px" }}
                          onClick={() => {
                            setEditProperty(null);
                            setContactType("");
                            setContactName("");
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </section>
          ) : (
            <>
              <section className="chartBox_area">
                <div className="row">
                  {/* <div className="col-md-2 mt-3">
                    <label htmlFor="startDate">Start Date</label>
                    <input
                      type="date"
                      name="startDate"
                      className="form-control"
                      value={filterData.startDate}
                      onChange={(e) =>
                        setFilterData({
                          ...filterData,
                          startDate: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="col-md-2 mt-3">
                    <label htmlFor="endDate">End Date</label>
                    <input
                      type="date"
                      name="endDate"
                      className="form-control"
                      value={filterData.endDate}
                      onChange={(e) =>
                        setFilterData({
                          ...filterData,
                          endDate: e.target.value,
                        })
                      }
                    />
                  </div> */}
                  <div className="col-md-2 mt-3">
                    <label>Property Type</label>
                    <select
                      className="form-control"
                      name="propertyType"
                      value={filterData?.propertyType}
                      onChange={(e) => {
                        setFilterData({
                          ...filterData,
                          propertyType: e.target.value,
                        });
                      }}
                    >
                      <option value="">---Select Property Type---</option>
                      {propertyTypes.map((e, key) => (
                        <option key={key} value={e?._id}>
                          {e?.propertyType}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-2 mt-3">
                    <label>For</label>
                    <select
                      className="form-control"
                      value={filterData?.propertyFor}
                      onChange={(e) =>
                        setFilterData({
                          ...filterData,
                          propertyFor: e.target.value,
                        })
                      }
                      required
                    >
                      <option value="">--Select--</option>
                      <option value="sales">Sale</option>
                      <option value="rent">Rent</option>
                    </select>
                  </div>
                  <div className="col-md-2 mt-3">
                    <label>City</label>
                    <select
                      className="form-control"
                      name="city"
                      value={filterData.city}
                      onChange={(e) => {
                        setFilterData({ ...filterData, city: e.target.value });
                      }}
                    >
                      <option value="">---Select City---</option>
                      {cities.map((city, key) => (
                        <option key={key} value={city._id}>
                          {city.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-2 mt-3">
                    <label>Locality</label>
                    <div className="custom-dropdown" ref={localityRef}>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search locality..."
                        value={
                          showLocalityDropdown
                            ? localitySearchTerm
                            : filterData.locality
                              ? localitiesForFilter.find(
                                  (item) => item._id === filterData.locality,
                                )?.name || ""
                              : ""
                        }
                        onChange={(e) => {
                          setLocalitySearchTerm(e.target.value);
                          setShowLocalityDropdown(true);
                        }}
                        onClick={() => {
                          setShowLocalityDropdown(!showLocalityDropdown);
                          setLocalitySearchTerm("");
                        }}
                      />
                      {showLocalityDropdown && (
                        <div
                          className="dropdown-list"
                          style={{
                            overflow: "auto",
                            scrollbarWidth: "none",
                          }}
                        >
                          <div
                            // key={item._id}
                            className={`dropdown-item ${
                              filterData.locality === "" ? "selected" : ""
                            }`}
                            onClick={() => {
                              setFilterData({
                                ...filterData,
                                locality: "",
                              });
                              setShowLocalityDropdown(false);
                              setLocalitySearchTerm("");
                            }}
                          >
                            Select Locality
                          </div>
                          {filteredLocalities.length > 0 ? (
                            filteredLocalities.map((item) => (
                              <div
                                key={item._id}
                                className={`dropdown-item ${
                                  filterData.locality === item._id
                                    ? "selected"
                                    : ""
                                }`}
                                onClick={() => {
                                  setFilterData({
                                    ...filterData,
                                    locality: item._id,
                                  });
                                  setShowLocalityDropdown(false);
                                  setLocalitySearchTerm("");
                                }}
                              >
                                {item.name}
                              </div>
                            ))
                          ) : (
                            <div className="dropdown-item no-results">
                              No localities found
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-md-2 mt-3">
                    <label htmlFor="name">Apartment Name</label>
                    <div className="custom-dropdown" ref={apartmentRef}>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search apartment..."
                        value={
                          showApartmentDropdown
                            ? apartmentSearchTerm
                            : filterData.name || ""
                        }
                        onChange={(e) => {
                          setApartmentSearchTerm(e.target.value);
                          setShowApartmentDropdown(true);
                        }}
                        onClick={() => {
                          setShowApartmentDropdown(!showApartmentDropdown);
                          setApartmentSearchTerm("");
                        }}
                      />
                      {showApartmentDropdown && (
                        <div
                          className="dropdown-list"
                          style={{
                            overflow: "auto",
                            scrollbarWidth: "none",
                          }}
                        >
                          <div
                            // key={key}
                            className={`dropdown-item ${
                              filterData.name === "" ? "selected" : ""
                            }`}
                            onClick={() => {
                              setFilterData({
                                ...filterData,
                                name: "",
                              });
                              setShowApartmentDropdown(false);
                              setApartmentSearchTerm("");
                            }}
                          >
                            Select Apartment
                          </div>
                          {filteredApartments.length > 0 ? (
                            filteredApartments.map((item, key) => (
                              <div
                                key={key}
                                className={`dropdown-item ${
                                  filterData.name === item.aptName
                                    ? "selected"
                                    : ""
                                }`}
                                onClick={() => {
                                  setFilterData({
                                    ...filterData,
                                    name: item.aptName,
                                  });
                                  setShowApartmentDropdown(false);
                                  setApartmentSearchTerm("");
                                }}
                              >
                                {item.aptName}
                              </div>
                            ))
                          ) : (
                            <div className="dropdown-item no-results">
                              No apartments found
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div
                    className="col-md-2 mt-3"
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "flex-end",
                      gap: "15px",
                    }}
                  >
                    <button
                      style={{
                        background: "rgb(133, 218, 79)",
                        borderRadius: "13px",
                        width: "fit-content",
                        height: "fit-content",
                      }}
                      type="submit"
                      className="btn mt-3"
                      onClick={(e) => filterPropertyList()}
                      disabled={isLoading}
                    >
                      {isLoading ? "Loading..." : "Filter"}
                    </button>
                    <button
                      style={{
                        background: "rgb(133, 218, 79)",
                        borderRadius: "13px",
                        width: "fit-content",
                        height: "fit-content",
                      }}
                      type="submit"
                      className="btn mt-3"
                      onClick={(e) => {
                        fetchProperties(1, false);
                        setFilterData({
                          startDate: "",
                          endDate: "",
                          city: userCity || "",
                          locality: "",
                          name: "",
                          propertyType: "",
                          propertyFor: "",
                        });
                        setCurrentPage(1);
                      }}
                      disabled={isLoading}
                    >
                      Clear
                    </button>
                  </div>
                </div>

                <div className="row">
                  <div
                    className="col-md-4 mt-3"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "flex-end",
                    }}
                  >
                    <input
                      type="text"
                      className="form-control"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      placeholder="Type to search"
                    />
                  </div>
                  <div
                    className="col-md-4 mt-3"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "flex-end",
                    }}
                  >
                    <button
                      style={{
                        background: "rgb(133, 218, 79)",
                        borderRadius: "13px",
                        width: "fit-content",
                        height: "fit-content",
                        display:
                          localStorage?.getItem("role") === "survey"
                            ? "none"
                            : "",
                      }}
                      className="btn mt-3 ml-2"
                      onClick={(e) => exportToExcel()}
                    >
                      Export ({properties.length || 0})
                    </button>
                  </div>
                </div>
                <div className="row">
                  <div className="col-lg-12 mt-3">
                    <div style={{ height: "65vh", overflow: "auto" }}>
                      <DataTable
                        columns={columns}
                        data={properties}
                        // pagination
                        // paginationPerPage={10}
                        // paginationRowsPerPageOptions={[10, 20, 30, 50]}
                        persistTableHead
                      />
                    </div>

                    {/* Custom Pagination for 1000 records */}
                    {totalPages > 1 && (
                      <div className="row mt-3">
                        <div className="col-md-12">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <p className="mb-0">
                                Showing page <strong>{currentPage}</strong> of{" "}
                                <strong>{totalPages}</strong> | Total
                                properties: <strong>{totalProperties}</strong>
                              </p>
                              <p className="mb-0 text-muted">
                                (Each page loads 1000 properties)
                              </p>
                            </div>
                            <div className="d-flex gap-2">
                              <button
                                className="btn btn-sm"
                                style={{
                                  background: "rgb(133, 218, 79)",
                                  color: "white",
                                  border: "none",
                                }}
                                onClick={loadPrevData}
                                disabled={!hasPrevPage || isLoading}
                              >
                                <i className="fas fa-chevron-left me-1"></i>
                                Previous 1000
                              </button>
                              <button
                                className="btn btn-sm"
                                style={{
                                  background: "rgb(133, 218, 79)",
                                  color: "white",
                                  border: "none",
                                }}
                                onClick={loadMoreData}
                                disabled={!hasNextPage || isLoading}
                              >
                                Next 1000
                                <i className="fas fa-chevron-right ms-1"></i>
                              </button>
                            </div>
                            <div>
                              <span className="badge bg-info">
                                Page {currentPage} of {totalPages}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </section>
            </>
          )}
        </div>
      </div>

      {/* Add CSS for dropdowns */}
      <style jsx>{`
        .custom-dropdown {
          position: relative;
          width: 100%;
        }
        .dropdown-list {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          max-height: 250px;
          overflow-y: auto;
          background: white;
          border: 1px solid #ddd;
          border-radius: 4px;
          margin-top: 2px;
          z-index: 1000;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        }
        .dropdown-item {
          padding: 8px 12px;
          cursor: pointer;
          transition: background-color 0.2s;
        }
        .dropdown-item:hover {
          background-color: #f0f0f0;
        }
        .dropdown-item.selected {
          background-color: #e3f2fd;
          color: #007bff;
        }
        .dropdown-item.no-results {
          color: #999;
          cursor: default;
        }
        .dropdown-item.no-results:hover {
          background-color: white;
        }
      `}</style>
    </div>
  );
}

export default ManageProperty;
